import React, { useState } from 'react';
import { PieChart, Pie, Cell, BarChart, Bar, LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const COLORS = ['#2196F3', '#4CAF50', '#FF9800', '#9C27B0', '#F44336', '#00BCD4', '#795548', '#607D8B', '#E91E63', '#3F51B5'];

// Enhanced Tooltip Component
const EnhancedTooltip = ({ active, payload, isCurrency }) => {
  if (!active || !payload || !payload[0]) return null;

  const data = payload[0].payload;
  const value = payload[0].value;

  return (
    <div style={{
      background: '#fff',
      border: '2px solid #2196F3',
      borderRadius: '8px',
      padding: '12px 16px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    }}>
      <div style={{ fontWeight: 700, marginBottom: '8px', color: '#333' }}>{data.name}</div>
      <div style={{ color: '#2196F3', fontSize: '20px', fontWeight: 700 }}>
        {isCurrency ? `$${value.toLocaleString()}` : value.toLocaleString()}
      </div>
      {data.pct !== undefined && (
        <div style={{ color: '#666', fontSize: '12px', marginTop: '4px' }}>
          {data.pct}% of total
        </div>
      )}
    </div>
  );
};

// Summary Stats Component
const SummaryStats = ({ data, isCurrency }) => {
  if (!data || data.length === 0) return null;

  const values = data.map(item => item.value || 0);
  const total = values.reduce((sum, val) => sum + val, 0);
  const average = total / values.length;
  const max = Math.max(...values);
  const min = Math.min(...values);

  const formatValue = (val) => {
    if (isCurrency) {
      return `$${val.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
    }
    return val.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  };

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
      gap: '12px',
      marginTop: '16px',
      marginBottom: '8px'
    }}>
      <StatCard label="Total" value={formatValue(total)} color="#2196F3" tooltip="Combined value across all categories — your overall business volume" />
      <StatCard label="Average" value={formatValue(average)} color="#4CAF50" tooltip="Average value per category — benchmark for performance comparison" />
      <StatCard label="Highest" value={formatValue(max)} color="#FF9800" tooltip="Top performing category — your strongest business area" />
      <StatCard label="Lowest" value={formatValue(min)} color="#9C27B0" tooltip="Lowest performing category — potential area for growth or attention" />
    </div>
  );
};

// Stat Card Component
const StatCard = ({ label, value, color, tooltip }) => (
  <div title={tooltip || label} style={{
    background: '#fafafa',
    border: '1px solid #e8e8e8',
    borderRadius: '8px',
    padding: '12px',
    textAlign: 'center',
    cursor: 'default'
  }}>
    <div style={{ fontSize: '11px', color: '#666', marginBottom: '4px', fontWeight: 600 }}>{label}</div>
    <div style={{ fontSize: '18px', fontWeight: 700, color: color }}>{value}</div>
  </div>
);

/**
 * IntelligenceChart — ENHANCED VERSION with 100% Visualization Coverage
 * 
 * Features:
 * - Line charts for time-series data
 * - Area charts for cumulative trends
 * - Summary stats (total, average, max, min)
 * - Progress bars in tables
 * - Enhanced tooltips
 * - View mode controls (chart/table/both)
 */
const IntelligenceChart = ({ answer, chartData, chartType, chartTitle, isCurrency, visualization }) => {
  const [viewMode, setViewMode] = useState('both'); // 'chart', 'table', 'both'
  
  if (!answer && !chartData) return null;

  // Priority 1: Pre-computed chart data from fast path
  if (chartData && chartData.length > 0) {
    const maxValue = Math.max(...chartData.map(item => item.value || 0));
    
    return (
      <div style={{ marginTop: '16px', marginBottom: '8px' }}>
        {/* View Mode Controls */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', justifyContent: 'flex-end' }}>
          <button 
            onClick={() => setViewMode('chart')}
            title="View business data as a visual chart — quickly spot trends, compare categories, and identify top performers"
            style={{
              padding: '6px 12px',
              fontSize: '11px',
              border: viewMode === 'chart' ? '2px solid #2196F3' : '1px solid #ddd',
              background: viewMode === 'chart' ? '#E3F2FD' : '#fff',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: viewMode === 'chart' ? 600 : 400
            }}
          >
            📈 Chart Only
          </button>
          <button 
            onClick={() => setViewMode('table')}
            title="View business data in a detailed table — see exact values, sort by columns, and review all records"
            style={{
              padding: '6px 12px',
              fontSize: '11px',
              border: viewMode === 'table' ? '2px solid #2196F3' : '1px solid #ddd',
              background: viewMode === 'table' ? '#E3F2FD' : '#fff',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: viewMode === 'table' ? 600 : 400
            }}
          >
            📊 Table Only
          </button>
          <button 
            onClick={() => setViewMode('both')}
            title="View both chart and table together — get the visual overview with detailed data side by side"
            style={{
              padding: '6px 12px',
              fontSize: '11px',
              border: viewMode === 'both' ? '2px solid #2196F3' : '1px solid #ddd',
              background: viewMode === 'both' ? '#E3F2FD' : '#fff',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: viewMode === 'both' ? 600 : 400
            }}
          >
            🔄 Both
          </button>
        </div>

        {/* Chart View */}
        {(viewMode === 'chart' || viewMode === 'both') && (
          <div style={{ background: '#fafafa', borderRadius: '10px', padding: '16px', border: '1px solid #e8e8e8', marginBottom: '12px' }}>
            {chartTitle && <div style={{ fontSize: '13px', fontWeight: 600, color: '#333', marginBottom: '12px' }}>{chartTitle}</div>}
            <ResponsiveContainer width="100%" height={chartType === 'pie' ? 280 : 240}>
              {chartType === 'pie' ? (
                <PieChart>
                  <Pie data={chartData} cx="50%" cy="50%" outerRadius={90} dataKey="value" label={({ name, pct }) => `${name} ${pct}%`}>
                    {chartData.map((_, idx) => <Cell key={idx} fill={COLORS[idx % COLORS.length]} />)}
                  </Pie>
                  <Tooltip content={<EnhancedTooltip isCurrency={isCurrency} />} />
                  <Legend />
                </PieChart>
              ) : chartType === 'line' ? (
                <LineChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={chartData.length > 10 ? -25 : 0} textAnchor={chartData.length > 10 ? "end" : "middle"} height={chartData.length > 10 ? 60 : 30} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip content={<EnhancedTooltip isCurrency={isCurrency} />} />
                  <Line 
                    type="monotone" 
                    dataKey="value" 
                    stroke="#2196F3" 
                    strokeWidth={3}
                    dot={{ fill: '#2196F3', r: 4 }}
                    activeDot={{ r: 6 }}
                    animationDuration={800}
                  />
                </LineChart>
              ) : chartType === 'area' ? (
                <AreaChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                  <defs>
                    <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#2196F3" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#2196F3" stopOpacity={0.1}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={chartData.length > 10 ? -25 : 0} textAnchor={chartData.length > 10 ? "end" : "middle"} height={chartData.length > 10 ? 60 : 30} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip content={<EnhancedTooltip isCurrency={isCurrency} />} />
                  <Area 
                    type="monotone" 
                    dataKey="value" 
                    stroke="#2196F3" 
                    strokeWidth={2}
                    fillOpacity={1} 
                    fill="url(#colorValue)"
                    animationDuration={800}
                  />
                </AreaChart>
              ) : (
                <BarChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={chartData.length > 10 ? -25 : 0} textAnchor={chartData.length > 10 ? "end" : "middle"} height={chartData.length > 10 ? 60 : 30} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip content={<EnhancedTooltip isCurrency={isCurrency} />} />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]} animationDuration={800}>
                    {chartData.map((_, idx) => <Cell key={idx} fill={COLORS[idx % COLORS.length]} />)}
                  </Bar>
                </BarChart>
              )}
            </ResponsiveContainer>
            
            {/* Summary Stats */}
            <SummaryStats data={chartData} isCurrency={isCurrency} />
          </div>
        )}

        {/* Table View with Progress Bars */}
        {(viewMode === 'table' || viewMode === 'both') && (
          <div style={{ background: '#fff', borderRadius: '10px', border: '1px solid #e8e8e8', overflow: 'auto', maxHeight: '400px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ background: '#f0f4f8', position: 'sticky', top: 0, zIndex: 1 }}>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#444', borderBottom: '2px solid #ddd' }}>Name</th>
                  <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, color: '#444', borderBottom: '2px solid #ddd' }}>Value</th>
                  <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, color: '#444', borderBottom: '2px solid #ddd' }}>%</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#444', borderBottom: '2px solid #ddd', width: '200px' }}>Visual</th>
                </tr>
              </thead>
              <tbody>
                {chartData.map((item, idx) => (
                  <tr key={idx} style={{ background: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                    <td style={{ padding: '8px 12px', borderBottom: '1px solid #eee', color: '#333' }}>{item.name}</td>
                    <td style={{ padding: '8px 12px', borderBottom: '1px solid #eee', textAlign: 'right', fontWeight: 600, color: '#2196F3' }}>
                      {isCurrency ? `$${item.value.toLocaleString()}` : item.value.toLocaleString()}
                    </td>
                    <td style={{ padding: '8px 12px', borderBottom: '1px solid #eee', textAlign: 'right', color: '#666' }}>
                      {item.pct}%
                    </td>
                    <td style={{ padding: '8px 12px', borderBottom: '1px solid #eee' }}>
                      <div style={{ width: '100%', height: '10px', background: '#e0e0e0', borderRadius: '5px', overflow: 'hidden' }}>
                        <div style={{
                          width: `${(item.value / maxValue) * 100}%`,
                          height: '100%',
                          background: `linear-gradient(90deg, ${COLORS[idx % COLORS.length]}, ${COLORS[(idx + 1) % COLORS.length]})`,
                          transition: 'width 0.8s ease-out',
                          borderRadius: '5px'
                        }} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  // Priority 2: Auto-detect JSON array in answer text
  const jsonChart = tryParseJsonChart(answer);
  if (jsonChart) {
    const maxValue = jsonChart.chartData.length > 0 ? Math.max(...jsonChart.chartData.map(item => item.value || 0)) : 0;
    
    return (
      <div style={{ marginTop: '16px', marginBottom: '8px' }}>
        {/* Chart (only if we have numeric data) */}
        {!jsonChart.tableOnly && jsonChart.chartData.length > 0 && (
          <div style={{ background: '#fafafa', borderRadius: '10px', padding: '16px', border: '1px solid #e8e8e8', marginBottom: '12px' }}>
            <div style={{ fontSize: '13px', fontWeight: 600, color: '#333', marginBottom: '12px' }}>{jsonChart.title}</div>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={jsonChart.chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-25} textAnchor="end" height={60} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip content={<EnhancedTooltip isCurrency={jsonChart.isCurrency} />} />
                <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                  {jsonChart.chartData.map((_, idx) => <Cell key={idx} fill={COLORS[idx % COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <SummaryStats data={jsonChart.chartData} isCurrency={jsonChart.isCurrency} />
          </div>
        )}
        <div style={{ background: '#fff', borderRadius: '10px', border: '1px solid #e8e8e8', overflow: 'auto', maxHeight: '320px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead>
              <tr style={{ background: '#f0f4f8', position: 'sticky', top: 0 }}>
                {jsonChart.columns.map((col, i) => (
                  <th key={i} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: '#444', borderBottom: '2px solid #ddd', whiteSpace: 'nowrap' }}>
                    {col.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {jsonChart.rows.map((row, ri) => (
                <tr key={ri} style={{ background: ri % 2 === 0 ? '#fff' : '#fafafa' }}>
                  {jsonChart.columns.map((col, ci) => {
                    const val = row[col];
                    const isNum = val != null && !isNaN(parseFloat(val)) && !col.includes('name');
                    return (
                      <td key={ci} style={{ padding: '6px 10px', borderBottom: '1px solid #eee', textAlign: isNum ? 'right' : 'left', color: '#333' }}>
                        {isNum && jsonChart.currencyCols.includes(col) ? `$${parseFloat(val).toFixed(2)}` : isNum && col.includes('pct') ? `${val}%` : String(val ?? '')}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // Priority 3: Parse text for chart data (LLM natural language responses)
  const charts = extractChartData(answer);
  if (charts.length === 0) return null;

  return (
    <div style={{ marginTop: '16px', marginBottom: '8px' }}>
      {charts.map((chart, i) => (
        <div key={i} style={{ background: '#fafafa', borderRadius: '10px', padding: '16px', marginBottom: '12px', border: '1px solid #e8e8e8' }}>
          {chart.title && <div style={{ fontSize: '13px', fontWeight: 600, color: '#333', marginBottom: '12px' }}>{chart.title}</div>}
          <ResponsiveContainer width="100%" height={chart.type === 'pie' ? 260 : 220}>
            {chart.type === 'pie' ? (
              <PieChart>
                <Pie data={chart.data} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name, pct }) => `${name} ${pct}%`}>
                  {chart.data.map((_, idx) => <Cell key={idx} fill={COLORS[idx % COLORS.length]} />)}
                </Pie>
                <Tooltip content={<EnhancedTooltip isCurrency={chart.isCurrency} />} />
                <Legend />
              </PieChart>
            ) : (
              <BarChart data={chart.data} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip content={<EnhancedTooltip isCurrency={chart.isCurrency} />} />
                <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                  {chart.data.map((_, idx) => <Cell key={idx} fill={COLORS[idx % COLORS.length]} />)}
                </Bar>
              </BarChart>
            )}
          </ResponsiveContainer>
          <SummaryStats data={chart.data} isCurrency={chart.isCurrency} />
        </div>
      ))}
    </div>
  );
};

/**
 * Try to parse a JSON array from the answer text and auto-generate chart + table data.
 * Returns null if the answer is not a JSON array.
 */
function tryParseJsonChart(text) {
  if (!text || typeof text !== 'string') return null;
  const trimmed = text.trim();

  // Must start with [ to be a JSON array
  if (!trimmed.startsWith('[')) return null;

  try {
    const rows = JSON.parse(trimmed);
    if (!Array.isArray(rows) || rows.length === 0 || typeof rows[0] !== 'object') return null;

    const columns = Object.keys(rows[0]);

    // Find the best label column (first string-like column)
    const labelCol = columns.find(c => c.includes('name') || c.includes('label') || c.includes('title')) || columns[0];

    // Find the best value column (prefer pct, margin, revenue, amount, value, count)
    const valuePriority = ['margin_pct', 'pct', 'margin', 'revenue', 'total', 'amount', 'value', 'count', 'shortage'];
    const valueCol = valuePriority.find(p => columns.some(c => c.includes(p))) 
      ? columns.find(c => valuePriority.some(p => c.includes(p)))
      : columns.find(c => {
          const sample = rows[0][c];
          return sample != null && !isNaN(parseFloat(sample)) && !c.includes('name');
        });

    if (!valueCol) {
      // No numeric column found — still return table-only data for visualization
      return { rows, columns, chartData: [], title: 'Query Results', isCurrency: false, currencyCols: [], labelCol, valueCol: null, tableOnly: true };
    }

    // Detect currency columns
    const currencyCols = columns.filter(c => 
      c.includes('price') || c.includes('cost') || c.includes('revenue') || 
      c.includes('margin') && !c.includes('pct') || c.includes('amount') || c.includes('total')
    );

    // Build chart data
    const chartData = rows.slice(0, 20).map(row => ({
      name: String(row[labelCol] || '').substring(0, 18),
      value: parseFloat(row[valueCol]) || 0,
    }));

    // Add percentages
    const total = chartData.reduce((sum, item) => sum + item.value, 0);
    chartData.forEach(item => {
      item.pct = total > 0 ? Math.round((item.value / total) * 100 * 10) / 10 : 0;
    });

    // Auto-detect title from column names
    const isCurrency = currencyCols.includes(valueCol);
    const isPct = valueCol.includes('pct');
    let title = 'Query Results';
    if (isPct) title = `${valueCol.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())} by ${labelCol.replace(/_/g, ' ')}`;
    else if (isCurrency) title = `${valueCol.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())} by ${labelCol.replace(/_/g, ' ')}`;
    else title = `${valueCol.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())} by ${labelCol.replace(/_/g, ' ')}`;

    return { rows, columns, chartData, title, isCurrency, currencyCols, labelCol, valueCol };
  } catch {
    return null;
  }
}

function extractChartData(text) {
  const charts = [];
  if (!text || typeof text !== 'string') return charts;
  const lines = text.split('\n');

  const revenuePattern = /[•\-\*📊💰⭐🏥🔴🟡⚡🧽🏠🏢📱🌐🚶📞🛵]\s*(.+?):\s*\$([0-9,]+(?:\.\d+)?[KMB]?)\s*(?:\((\d+(?:\.\d+)?)%\))?/;
  const pctPattern = /[•\-\*📊🔄]\s*(.+?):\s*(\d+(?:\.\d+)?)%/;
  const countPattern = /[•\-\*📊👥🏪🛒]\s*(.+?):\s*(\d+(?:,\d+)*)\s*(orders?|jobs?|calls?|sessions?|visits?|leads?|listings?|units?|contracts?|policies?|members?|patients?|vehicles?|reservations?|reviews?|closings?|shipments?|treatments?)/i;
  const ratingPattern = /[•\-\*⭐🏆]\s*(?:\d+\.\s*)?(.+?)\s*[—–-]\s*(\d+(?:\.\d+)?)\/5\.0/;

  let revenueItems = [];
  let pctItems = [];
  let countItems = [];
  let ratingItems = [];
  let chartTitle = '';

  for (const line of lines) {
    if (line.match(/^[A-Z📊💰🏆⭐🔄📋📅⏱️⚡🧽🏠📱]/)) {
      const titleMatch = line.match(/^[^:•\-\*]+(?::|—)/);
      if (titleMatch && !line.includes('$') && !line.includes('%')) {
        chartTitle = titleMatch[0].replace(/[:\u2014\u2013-]\s*$/, '').trim();
      }
    }

    const revMatch = line.match(revenuePattern);
    if (revMatch) {
      let val = revMatch[2].replace(/,/g, '');
      if (val.endsWith('K')) val = parseFloat(val) * 1000;
      else if (val.endsWith('M')) val = parseFloat(val) * 1000000;
      else if (val.endsWith('B')) val = parseFloat(val) * 1000000000;
      else val = parseFloat(val);
      const pct = revMatch[3] ? parseFloat(revMatch[3]) : 0;
      revenueItems.push({ name: revMatch[1].trim().substring(0, 20), value: val, pct });
      continue;
    }

    const pctMatch = line.match(pctPattern);
    if (pctMatch && !line.match(revenuePattern)) {
      pctItems.push({ name: pctMatch[1].trim().substring(0, 20), value: parseFloat(pctMatch[2]), pct: parseFloat(pctMatch[2]) });
      continue;
    }

    const countMatch = line.match(countPattern);
    if (countMatch) {
      countItems.push({ name: countMatch[1].trim().substring(0, 20), value: parseInt(countMatch[2].replace(/,/g, ''), 10), pct: 0 });
      continue;
    }

    const ratingMatch = line.match(ratingPattern);
    if (ratingMatch) {
      ratingItems.push({ name: ratingMatch[1].trim().substring(0, 20), value: parseFloat(ratingMatch[2]), pct: 0 });
    }
  }

  if (revenueItems.length >= 2) {
    charts.push({ type: revenueItems.length <= 6 ? 'pie' : 'bar', title: chartTitle || 'Revenue Breakdown', data: revenueItems, isCurrency: true });
  }
  if (pctItems.length >= 2 && revenueItems.length === 0) {
    charts.push({ type: pctItems.length <= 6 ? 'pie' : 'bar', title: chartTitle || 'Percentage Breakdown', data: pctItems, isCurrency: false });
  }
  if (countItems.length >= 2 && revenueItems.length === 0) {
    charts.push({ type: 'bar', title: chartTitle || 'Volume Comparison', data: countItems, isCurrency: false });
  }
  if (ratingItems.length >= 3 && charts.length === 0) {
    charts.push({ type: 'bar', title: chartTitle || 'Performance Ratings', data: ratingItems, isCurrency: false });
  }

  return charts;
}

export default IntelligenceChart;
